import { useEffect, useMemo, useState } from "react";

import { InformationCircleIcon, KeyIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ILead } from "../../models/Lead";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import LoadingWheel from "../shared/LoadingWheel";
import useCategories from "../../data/useCategories";
import usePartAssembly from "../../data/usePartAssembly";
import useVehicleCategorySummaries, { IVehicleCategoryRow } from "../../data/useVehicleCategorySummaries";
import useVehicleCategoryStockTable from "../../data/useVehicleCategoryStockTable";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useVehicleSingle from "../../data/useVehicleSingle";
import useCustomerDetails from "../../data/useCustomerDetails";
import usePartsLeadLookup from "../../data/usePartsLeadLookup";
import useNotification from "../notifications/useNotifications";
import PartCategoryCombo from "../leads/LeadParts/PartCategoryCombo";
import usePartsMarketDataLookup from "../../data/usePartsMarketDataLookup";
import { IMarketDataLookupResponse, IPartDataResponse, PartProBlackList } from "../../models/PartLookup";
import SelectedCategories from "../leads/LeadParts/LeadPartsCategories/SelectedCategories";
import { CategoryWithParts, ILeadPartsTableGlobalFilters, IPart, IPartAssembly, IPartCategory, IVehicleCategoryPartsFilters, PartStatus } from "../../models/Part";
import LeadPartsCategories from "../leads/LeadParts/LeadPartsCategories/LeadPartsCategories";
import ManualLeadPartCategories from "../leads/LeadParts/LeadPartsCategories/ManualLeadPartCategories";
import useSingleSetting from "../../data/useSingleSetting";
import ButtonGroup from "../shared/ButtonGroup";
import SearchByFiltersModal, { SearchByOption } from "../shared/Tables/SearchByFiltersModal";
import { filterTopCategories } from "./topCategoryFilters";
import useRsCategoryLookup from "./useRsCategoryLookup";
import PreviousPartListModal from "./PreviousPartListModal";
import usePreviousPartListCategories from "./usePreviousPartListCategories";
import PartsInStockTable from "./PartsInStockTable";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

export default function CreateJobs({ lead, vehicle: inputVehicle, hidePartsInStock = false }: { lead: ILead | null, vehicle: IVehicle | null, hidePartsInStock?: boolean }) {
  const hasTmeFeature = CheckFeatureFlag("TME");
  const [currentVehicle, setCurrentVehicle] = useState<IVehicle | null>(lead?.vehicle ?? inputVehicle ?? null);
  const vehicle = currentVehicle ?? lead?.vehicle ?? inputVehicle ?? null;

  const { assemblies } = usePartAssembly();
  const { categories: allCategories } = useCategories();
  const { lookup, lookupMutation } = usePartsLeadLookup(lead?.id, vehicle?.id ?? lead?.vehicleId ?? "", lead?.partsLookupRequestId ?? vehicle?.partsLookupRequestId ?? lead?.vehicle?.partsLookupRequestId, "CreateJobs");
  const { vehicle: singleVehicle } = useVehicleSingle(lead?.vehicleId ?? vehicle?.id ?? undefined);
  const { marketData } = usePartsMarketDataLookup(vehicle?.id ?? lead?.vehicle?.id ?? undefined);
  const { brand } = useCustomerDetails();

  const { addNotification } = useNotification();

  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    setCurrentVehicle(lead?.vehicle ?? inputVehicle ?? null);
  }, [lead?.vehicle, inputVehicle]);

  useEffect(() => {
    if (!singleVehicle.data?.data) return;

    setCurrentVehicle(singleVehicle.data.data);
  }, [singleVehicle.data?.data]);


  const [addManualOpen, setAddManualOpen] = useState(false);
  const [partProOpen, setPartProOpen] = useState(false);
  const [partProLoading, setPartProLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const [rsOpen, setRsOpen] = useState(false);

  const [categories, setCategories] = useState<CategoryWithParts[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<CategoryWithParts[]>([]);
  const [sortedCategories, setSortedCategories] = useState<CategoryWithParts[]>([]);
  const [selectedByTableId, setSelectedByTableId] = useState<Record<string, CategoryWithParts[]>>({});

  const { singleSetting } = useSingleSetting("Rs:ApiKey");
  const [hasRsKey, setHasRsKey] = useState(false);
  const [rsApiKey, setRsApiKey] = useState<string | null>(null);

  const [tab, setTab] = useState<"all" | "rs">("all");

  const { handleRsLookup, rsLoading, rsTotal } = useRsCategoryLookup({
    lead,
    vehicle,
    categories,
    rsApiKey,
    setCategories,
    onLookupComplete: () => setRsOpen(false),
  });

  const getSelectedPartStatuses = (searchBy: string[]): PartStatus[] => {
    const statusOptionMap: Record<string, PartStatus> = {
      partStatusOnTheCar: PartStatus["On The Car"],
      partStatusPendingDismantle: PartStatus["Pending Dismantle"],
      partStatusDismantled: PartStatus.Dismantled,
      partStatusDamaged: PartStatus.Damaged,
      partStatusQc: PartStatus.QC,
      partStatusMissing: PartStatus.Missing,
      partStatusSold: PartStatus.Sold,
      partStatusDeleted: PartStatus.Deleted,
      partStatusReturned: PartStatus.Returned,
    };

    return searchBy
      .map((value) => statusOptionMap[value])
      .filter((status): status is PartStatus => typeof status !== "undefined");
  };

  const bodySearchOptions: SearchByOption[] = useMemo(() => [
    { label: "Match make", value: "matchMake" },
    { label: "Match model", value: "matchModel" },
    { label: "Match KType", value: "matchKType" },
    { label: "Match series", value: "matchSeries" },
    { label: "Match MK", value: "matchMk" },
    { label: "Match body style", value: "matchBodyStyle" },
    { label: "Match doors", value: "matchDoors" },
    { label: "Match exact year", value: "matchExactYear" },
    { label: "Match year range", value: "matchYearRange" },
  ], []);

  const mechSearchOptions: SearchByOption[] = useMemo(() => [
    { label: "Match make", value: "matchMake" },
    { label: "Match model", value: "matchModel" },
    { label: "Match KType", value: "matchKType" },
    { label: "Match series", value: "matchSeries" },
    { label: "Match fuel type", value: "matchFuelType" },
    { label: "Match engine code", value: "matchEngineCode" },
    { label: "Match engine size", value: "matchEngineSize" },
    { label: "Match exact year", value: "matchExactYear" },
    { label: "Match year range", value: "matchYearRange" },
  ], []);

  const categoryStatusSearchOptions: SearchByOption[] = useMemo(() => [
    { label: "Dismantled", value: "partStatusDismantled" },
    { label: "Sold", value: "partStatusSold" },
    { label: "On The Car", value: "partStatusOnTheCar" },
    { label: "Pending Dismantle", value: "partStatusPendingDismantle" },
    { label: "Damaged", value: "partStatusDamaged" },
    { label: "QC", value: "partStatusQc" },
    { label: "Missing", value: "partStatusMissing" },
    { label: "Deleted", value: "partStatusDeleted" },
    { label: "Returned", value: "partStatusReturned" },
  ], []);

  const [bodySearchBy, setBodySearchBy] = useState<string[]>([
    "matchMake",
    "matchModel",
    "matchBodyStyle",
    "matchDoors",
    "matchExactYear",
    "matchYearRange",
  ]);

  const [mechSearchBy, setMechSearchBy] = useState<string[]>([
    "matchMake",
    "matchModel",
    "matchFuelType",
    "matchEngineCode",
    "matchEngineSize",
    "matchExactYear",
    "matchYearRange",
  ]);

  const [categoryStatusSearchBy, setCategoryStatusSearchBy] = useState<string[]>([
    "partStatusDismantled",
    "partStatusSold",
  ]);

  const getDefaultGlobalYearRange = () => {
    const sourceVehicle = vehicle;
    const fallbackYear = sourceVehicle?.dvlaYearOfManufacture ?? undefined;
    const visibilityYear = sourceVehicle?.visibilityDate ? new Date(sourceVehicle.visibilityDate).getFullYear() : undefined;
    const terminateYear = sourceVehicle?.terminateDate ? new Date(sourceVehicle.terminateDate).getFullYear() : undefined;

    return {
      from: visibilityYear ?? fallbackYear,
      to: terminateYear ?? fallbackYear,
    };
  };

  const initialGlobalRange = getDefaultGlobalYearRange();
  const [bodyYearFrom, setBodyYearFrom] = useState<number | undefined>(initialGlobalRange.from);
  const [bodyYearTo, setBodyYearTo] = useState<number | undefined>(initialGlobalRange.to);
  const [draftBodyYearFrom, setDraftBodyYearFrom] = useState<number | undefined>(initialGlobalRange.from);
  const [draftBodyYearTo, setDraftBodyYearTo] = useState<number | undefined>(initialGlobalRange.to);
  const [mechYearFrom, setMechYearFrom] = useState<number | undefined>(initialGlobalRange.from);
  const [mechYearTo, setMechYearTo] = useState<number | undefined>(initialGlobalRange.to);
  const [draftMechYearFrom, setDraftMechYearFrom] = useState<number | undefined>(initialGlobalRange.from);
  const [draftMechYearTo, setDraftMechYearTo] = useState<number | undefined>(initialGlobalRange.to);

  const options = useMemo(
    () => [
      { id: "all", label: "All Categories", value: "all" },
      { id: "rs", label: "RS Categories", value: "rs" },
    ],
    []
  );

  const selectedIndex = tab === "all" ? 0 : 1;

  const filter = useMemo(() => {
    return tab === "all"
      ? (() => true)
      : ((cat: CategoryWithParts) => cat.category.rsPrice != null);
  }, [tab])

  useEffect(() => {
    const nextRange = getDefaultGlobalYearRange();
    setBodyYearFrom(nextRange.from);
    setBodyYearTo(nextRange.to);
    setDraftBodyYearFrom(nextRange.from);
    setDraftBodyYearTo(nextRange.to);
    setMechYearFrom(nextRange.from);
    setMechYearTo(nextRange.to);
    setDraftMechYearFrom(nextRange.from);
    setDraftMechYearTo(nextRange.to);
  }, [vehicle?.id, vehicle?.visibilityDate, vehicle?.terminateDate, vehicle?.dvlaYearOfManufacture]);

  const globalSummaryFilters = useMemo<IVehicleCategoryPartsFilters>(() => ({
    bodyFilters: {
      matchMake: bodySearchBy.includes("matchMake"),
      matchModel: bodySearchBy.includes("matchModel"),
      matchKType: bodySearchBy.includes("matchKType"),
      matchSeries: bodySearchBy.includes("matchSeries"),
      matchMk: bodySearchBy.includes("matchMk"),
      matchBodyStyle: bodySearchBy.includes("matchBodyStyle"),
      matchDoors: bodySearchBy.includes("matchDoors"),
      matchExactYear: bodySearchBy.includes("matchExactYear"),
      matchYearRange: bodySearchBy.includes("matchYearRange"),
      yearFrom: bodyYearFrom,
      yearTo: bodyYearTo,
      partStatuses: getSelectedPartStatuses(categoryStatusSearchBy),
    },
    mechFilters: {
      matchMake: mechSearchBy.includes("matchMake"),
      matchModel: mechSearchBy.includes("matchModel"),
      matchKType: mechSearchBy.includes("matchKType"),
      matchSeries: mechSearchBy.includes("matchSeries"),
      matchFuelType: mechSearchBy.includes("matchFuelType"),
      matchEngineCode: mechSearchBy.includes("matchEngineCode"),
      matchEngineSize: mechSearchBy.includes("matchEngineSize"),
      matchExactYear: mechSearchBy.includes("matchExactYear"),
      matchYearRange: mechSearchBy.includes("matchYearRange"),
      yearFrom: mechYearFrom,
      yearTo: mechYearTo,
      partStatuses: getSelectedPartStatuses(categoryStatusSearchBy),
    },
  }), [bodySearchBy, mechSearchBy, bodyYearFrom, bodyYearTo, mechYearFrom, mechYearTo, categoryStatusSearchBy]);

  const globalSearchVersion = useMemo(() => JSON.stringify({
    bodySearchBy: [...bodySearchBy].sort(),
    mechSearchBy: [...mechSearchBy].sort(),
    categoryStatusSearchBy: [...categoryStatusSearchBy].sort(),
    bodyYearFrom,
    bodyYearTo,
    mechYearFrom,
    mechYearTo,
  }), [bodySearchBy, mechSearchBy, categoryStatusSearchBy, bodyYearFrom, bodyYearTo, mechYearFrom, mechYearTo]);

  const leadPartsTableFilters = useMemo<ILeadPartsTableGlobalFilters>(
    () => ({
      bodySearchBy,
      bodyYearFrom,
      bodyYearTo,
      mechSearchBy,
      mechYearFrom,
      mechYearTo,
      categoryStatusSearchBy,
      searchVersion: globalSearchVersion,
    }),
    [
      bodySearchBy,
      bodyYearFrom,
      bodyYearTo,
      mechSearchBy,
      mechYearFrom,
      mechYearTo,
      categoryStatusSearchBy,
      globalSearchVersion,
    ]
  );

  const { summaries: categorySummaries } = useVehicleCategorySummaries(
    vehicle?.id ?? lead?.vehicle?.id ?? undefined,
    globalSummaryFilters
  );
  const { stockTable: categoryStockTable } = useVehicleCategoryStockTable(
    vehicle?.id ?? lead?.vehicle?.id ?? undefined,
    globalSummaryFilters
  );

  useEffect(() => {
    if (singleSetting.data?.data && singleSetting.data.data.value != "" && singleSetting.data.data.value != null) {
      setHasRsKey(true);
      setRsApiKey(singleSetting.data?.data?.value);
    } else {
      setHasRsKey(false);
      setRsApiKey(null);
    }
  }, [singleSetting.data, singleSetting.isLoading]);

  useEffect(() => {
    if (rsApiKey == null || rsApiKey == "" || isLoading) return;

    handleRsLookup(true);
  }, [rsApiKey, isLoading]);

  type SelectionUpdater = | CategoryWithParts[] | ((prevForThisTable: CategoryWithParts[]) => CategoryWithParts[]);

  const hasLookup = useMemo(() => {
    // !!lead?.partsLookupRequestId is true if the lead has a parts lookup request ID and same for vehicle
    return !!lead?.partsLookupRequestId || !!vehicle?.partsLookupRequestId;
  }, [lead?.partsLookupRequestId, vehicle?.partsLookupRequestId]);
  const partProUnlocked = useMemo(() => {
    return hasLookup || !!lookup.data?.data?.requestId;
  }, [hasLookup, lookup.data?.data?.requestId]);


  const handleTableSelectionChange = (tableId: string, updater: SelectionUpdater) => {
    setSelectedByTableId((prevByTable) => {
      //Figure out what *used to be* selected for this exact table:
      const prevForThisTable = prevByTable[tableId] ?? [];

      //Compute what *should be* selected now for this table:
      const nextForThisTable: CategoryWithParts[] =
        typeof updater === "function"
          ? updater(prevForThisTable)
          : updater;

      //Create a new copy of the “byTable” map:
      const updatedByTable = {
        ...prevByTable,
        [tableId]: nextForThisTable,
      };

      //Flatten all of the table‐specific arrays into one global list:
      const allSelected = Object.values(updatedByTable).flat();

      //Update full list of selected categories
      setSelectedCategories(allSelected);

      return updatedByTable;
    });
  };

  const handleAddCategory = (category: IPartCategory) => {
    let isMech = true;

    if (!category) return;

    const enrichedCategory = getCategoryWithMasterNames(category);

    if (enrichedCategory.mechOnly === false && enrichedCategory.partOnly === true) {
      isMech = false;
    };

    let newCat: CategoryWithParts = {
      category: enrichedCategory,
      parts: [],
      isMech: isMech,
    };

    //Check if this category is already here
    const existingCategory = categories?.find(c => c.category.id === newCat.category.id);

    // console.log("Adding category", newCat.category.name, "to", existingCategory ? "existing" : "new");

    if (existingCategory) {
      // If it exists, find which assembly it belongs to
      const assembly = assemblies.data?.data.find((a: IPartAssembly) =>
        a.partAssemblyPartCategories?.some((cat) => cat.partCategoryId === existingCategory.category.id)
      );

      // If it belongs to an assembly, select that assembly
      // Otherwise, select it as unmatched
      if (assembly) {
        handleTableSelectionChange(assembly.id, [existingCategory]);
      } else {
        handleTableSelectionChange("unmatched", [existingCategory]);
      }
    } else {
      // If it doesn't exist, add it to the manual selection
      const currentManualSelection = selectedByTableId["manual"] || [];
      const isAlreadySelected = currentManualSelection.some(cat => cat.category.id === newCat.category.id);

      if (!isAlreadySelected) {
        newCat.isManual = true; //So we can filter them out for the manual table
        handleTableSelectionChange("manual", [...currentManualSelection, newCat]);
      }
    }
    setAddManualOpen(false);

  };

  const setTopCategories = (count: number) => {
    const sourceVehicle = vehicle ?? singleVehicle.data?.data ?? null;
    const toAdd = filterTopCategories(sortedCategories, sourceVehicle).slice(0, count);
    // console.log("toAdd", toAdd);
    // console.log("sortedCategories prices", sortedCategories.map(c => ({
    //   name: c.category.name,
    //   lastSoldPrice: c.lastSoldPrice ?? c.category.lastSoldPrice,
    //   marketDataAveragePrice: c.category.marketDataAveragePrice,
    //   lookupAveragePrice: c.category.lookupAveragePrice,
    //   max: Math.max(
    //     c.lastSoldPrice ?? c.category.lastSoldPrice ?? 0,
    //     c.category.marketDataAveragePrice || 0,
    //     c.category.lookupAveragePrice || 0
    //   )
    // })));

    // Calculate prices for all categories to add
    const categoriesWithPrices = toAdd.map(cat => {
      // Calculate the highest potential price for this category
      const lastSoldPrice = cat.lastSoldPrice ?? cat.category.lastSoldPrice ?? 0;
      const maxPrice = Math.max(
        lastSoldPrice,
        cat.category.marketDataAveragePrice || 0,
        cat.category.lookupAveragePrice || 0,
        cat.category.rsPrice || 0
      );

      return {
        categoryId: cat.category.id,
        price: maxPrice > 0 ? maxPrice : undefined
      };
    });

    // Update categories state with prices first, so table displays them
    setCategories(prev => {
      return prev.map(cat => {
        const priceInfo = categoriesWithPrices.find(p => p.categoryId === cat.category.id);
        if (priceInfo && priceInfo.price) {
          // Always update with the calculated max price for top categories
          return { ...cat, price: priceInfo.price };
        }
        return cat;
      });
    });

    // Keep a local copy of the current selections
    const currentSelections = { ...selectedByTableId };

    toAdd.forEach(cat => {
      const category = cat.category;
      if (!category) return;

      const isMech =
        category.mechOnly === false && category.partOnly === true ? false : true;

      // Calculate the highest potential price for this category (same calculation as above)
      const lastSoldPrice = cat.lastSoldPrice ?? category.lastSoldPrice ?? 0;
      const maxPrice = Math.max(
        lastSoldPrice,
        category.marketDataAveragePrice || 0,
        category.lookupAveragePrice || 0,
        category.rsPrice || 0
      );

      // Find the matching category from current categories state to get the updated price
      const existingCategory = categories?.find(
        c => c.category.id === category.id
      );

      const targetCategory: CategoryWithParts = {
        category,
        parts: existingCategory?.parts || [],
        isMech,
        price: maxPrice > 0 ? maxPrice : (existingCategory?.price || undefined), // Use calculated price or existing
        internalStock: existingCategory?.internalStock,
        totalSold: existingCategory?.totalSold,
        lastSoldDate: existingCategory?.lastSoldDate,
        lastSoldPrice: existingCategory?.lastSoldPrice,
      };

      // Find which assembly (if any) this category belongs to
      const assembly = assemblies.data?.data.find((a: IPartAssembly) =>
        a.partAssemblyPartCategories?.some(
          cat => cat.partCategoryId === targetCategory.category.id
        )
      );

      // Determine which table this should go to
      const targetTableId = assembly ? assembly.id : "unmatched";

      // Initialize list for this table if needed
      const currentList = currentSelections[targetTableId] || [];

      // Only add if not already selected
      const alreadySelected = currentList.some(
        c => c.category.id === targetCategory.category.id
      );

      if (!alreadySelected) {
        currentSelections[targetTableId] = [
          ...currentList,
          targetCategory,
        ];
      }
    });

    Object.entries(currentSelections).forEach(([tableId, cats]) => {
      handleTableSelectionChange(tableId, cats);
    });

    setAddManualOpen(false);
  };

  const setRsCategories = () => {
    // Keep a local copy of the current selections
    const currentSelections = { ...selectedByTableId };

    // Pick any categories that have an RS price
    const rsCats = (categories ?? []).filter(c => (c.category?.rsPrice ?? 0) > 0);

    rsCats.forEach(cat => {
      const category = cat.category;
      if (!category) return;

      // Match your existing "isMech" rule
      const isMech =
        category.mechOnly === false && category.partOnly === true ? false : true;

      // Ensure the object matches the shape you store in selections
      const targetCategory: CategoryWithParts = {
        category,
        parts: cat.parts || [],
        isMech,
        price: cat.category.rsPrice ?? 0,
        isCore: true,               // keep your current calculated price if any
        internalStock: cat.internalStock,
        totalSold: cat.totalSold,
        lastSoldDate: cat.lastSoldDate,
        lastSoldPrice: cat.lastSoldPrice,
      };

      // Find which assembly (if any) this category belongs to
      const assembly = assemblies.data?.data.find((a: IPartAssembly) =>
        a.partAssemblyPartCategories?.some(pc => pc.partCategoryId === targetCategory.category.id)
      );

      // Determine which table this should go to
      const targetTableId = assembly ? assembly.id : "unmatched";

      // Initialize list for this table if needed
      const currentList = currentSelections[targetTableId] || [];

      const existingIndex = currentList.findIndex(c => c.category.id === targetCategory.category.id);

      if (existingIndex === -1) {
        // not selected yet → add
        currentSelections[targetTableId] = [...currentList, targetCategory];
      } else {
        // already selected → override if incoming is RS
        const existing = currentList[existingIndex];

        const shouldOverride = !!targetCategory.category.rsPrice; // or use a stronger RS flag if you have one

        if (shouldOverride) {
          const next = [...currentList];
          next[existingIndex] = {
            ...existing,          // keep any user selections / local fields
            ...targetCategory,    // apply RS values
            price: targetCategory.category.rsPrice ?? existing.price ?? 0,
            isCore: true,
          };
          currentSelections[targetTableId] = next;
        }
      }
    });

    // Apply selection updates per table
    Object.entries(currentSelections).forEach(([tableId, cats]) => {
      handleTableSelectionChange(tableId, cats as CategoryWithParts[]);
    });

    setCategories((prev: any) =>
      prev.map((cat: any) =>
        (cat.category?.rsPrice ?? 0) > 0
          ? { ...cat, price: cat.category.rsPrice } // <-- set displayed price to RS price
          : cat
      )
    );

    setAddManualOpen(false);
  };

  const getCategoryWithMasterNames = (category: IPartCategory): IPartCategory => {
    const master = allCategories.data?.data?.find((c) => c.id === category.id);
    if (!master) return category;

    return {
      ...category,
      rsName: category.rsName || master.rsName || "",
      pinnacleName: category.pinnacleName || master.pinnacleName || "",
      frontierName: category.frontierName || master.frontierName || "",
    };
  };

  const {
    previousPartListOpen,
    setPreviousPartListOpen,
    previousPartListData,
    previousPartListsData,
    selectedPreviousJobId,
    setSelectedPreviousJobId,
    fetchPreviousPartListCategories,
    applyPreviousPartListCategories,
    previousPartListLoading,
  } = usePreviousPartListCategories({
    lead,
    vehicle,
    categories,
    allCategories: allCategories.data?.data,
    assemblies: assemblies.data?.data,
    selectedByTableId,
    setCategories,
    onTableSelectionChange: handleTableSelectionChange,
    getCategoryWithMasterNames,
  });


  const buildFromVehicleParts = (): CategoryWithParts[] => {
    // Use category summaries endpoint (fast, optimized)
    if (!categorySummaries.data?.data || !singleVehicle.data) return [];

    const summaries = categorySummaries.data.data;
    let builtCategories: CategoryWithParts[] = [];

    summaries.forEach((summary: IVehicleCategoryRow) => {
      const category: IPartCategory = {
        id: summary.categoryId,
        name: summary.categoryName,
        partOnly: summary.partOnly,
        mechOnly: summary.mechOnly,
        postage: summary.postage,
        whCost: summary.whCost,
        whTime: summary.whTime,
        disCost: summary.disCost,
        disTime: summary.disTime,
        pickingTime: summary.pickingTime,
        packingTime: summary.packingTime,
        notes: summary.notes,
        safetyNote: "",
        dismantlingInstructions: "",
        storageNote: "",
        skuPrefix: summary.skuPrefix,
        ucid: summary.ucid,
        ucidZone: summary.ucidZone,
        rsName: "",
        pinnacleName: "",
        frontierName: "",
        side: 0,
        position: 0,
        partMappings: [],
        partCategoryDefaults: [],
        partAssemblyPartCategories: undefined,
        partImageRequirementGroupId: null,
        partImageRequirementGroup: undefined,
        // Frontend-only fields
        averagePrice: 0,
        averageMargin: 0,
        rsPrice: null,
        rsItem: null,
        rsData: null,
        lookupAveragePrice: null,
        marketDataAveragePrice: null,
        marketDataSold: null,
        lastSoldPrice: summary.lastSoldPrice,
        lookupPartNumber: null,
        // BaseEntity fields
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
      };

      builtCategories.push({
        category: getCategoryWithMasterNames(category),
        parts: [], // Parts will be loaded on-demand when row is expanded
        isMech: summary.mechOnly !== false && summary.partOnly !== true,
        // Add aggregate fields
        internalStock: summary.internalStock,
        totalSold: summary.totalSold,
        lastSoldDate: summary.lastSoldDate,
        lastSoldPrice: summary.lastSoldPrice,
      });
    });

    // Merge assembly categories with existing categories, but take out duplicates
    var assemblyCategories: CategoryWithParts[] = [];

    if (assemblies.data?.data && assemblies.data?.data?.length > 0) {
      // Loop through each assembly and find matching categories
      assemblies.data.data.forEach((assembly: IPartAssembly) => {
        assembly.partAssemblyPartCategories?.forEach((cat) => {
          assemblyCategories.push({
            category: getCategoryWithMasterNames(cat.partCategory!),
            parts: [],
            isMech: cat.partCategory?.mechOnly !== false && cat.partCategory?.partOnly !== true,
          });
        });
      });

      if (assemblyCategories.length > 0) {
        // Remove duplicates inside assembly categories first, then remove any already present in builtCategories.
        const uniqueAssemblyById = new Map<string, CategoryWithParts>();
        assemblyCategories.forEach((cat) => {
          if (!uniqueAssemblyById.has(cat.category.id)) {
            uniqueAssemblyById.set(cat.category.id, cat);
          }
        });

        assemblyCategories = [...uniqueAssemblyById.values()].filter((cat) => {
          return !builtCategories.some((c) => c.category.id === cat.category.id);
        });

        builtCategories = [...builtCategories, ...assemblyCategories];
      }
    }

    return builtCategories;
  }

  function mergeLookup(categories: CategoryWithParts[], lookupResponse: IPartDataResponse | undefined): CategoryWithParts[] {
    if (!lookupResponse?.requestedParts?.length) return categories;

    const updated = [...categories];
    lookupResponse.requestedParts.forEach(part => {
      if (!part.partCategory) return;

      const existing = updated.find(c => c.category.id === part.partCategory!.id);
      if (existing) {
        existing.category = getCategoryWithMasterNames({
          ...existing.category,
          lookupAveragePrice: part?.priceInformation?.avgPrice ?? 0,
          lookupPartNumber: part?.partNumber ?? null,
        });
      } else {
        updated.push({
          category: getCategoryWithMasterNames({
            ...part.partCategory,
            lookupAveragePrice: part?.priceInformation?.avgPrice ?? 0,
            lookupPartNumber: part?.partNumber ?? null,
          }),
          parts: [],
          isMech: part.partCategory.mechOnly === true,
        });
      }
    });

    return updated;
  }

  function mergeMarketData(categories: CategoryWithParts[], response: IMarketDataLookupResponse | undefined, allCats: IPartCategory[]): CategoryWithParts[] {
    if (!response?.categories?.length) return categories;

    const updated = [...categories];

    response.categories.forEach(md => {
      const baseCat = allCats.find(c => c.id === md.categoryId);
      if (!baseCat) return;

      const existing = updated.find(c => c.category.id === md.categoryId);
      if (existing) {
        existing.category = getCategoryWithMasterNames({
          ...existing.category,
          marketDataAveragePrice: md.avgPrice ?? 0,
          marketDataSold: md.quantity ?? 0,
        });
      } else {
        updated.push({
          category: getCategoryWithMasterNames({
            ...baseCat,
            marketDataAveragePrice: md.avgPrice ?? 0,
            marketDataSold: md.quantity ?? 0,
          }),
          parts: [],
          isMech: baseCat.mechOnly === true,
        });
      }
    });

    return updated;
  }

  const baseCategories = useMemo(() => {
    if (!categorySummaries.data?.data || !singleVehicle.data) return [];

    let built = buildFromVehicleParts();

    built = mergeLookup(built, lookup.data?.data);
    built = mergeMarketData(built, marketData.data?.data, allCategories.data?.data ?? []);

    // get ids of parts already on the vehicle
    const existingCategoryIds = new Set(
      singleVehicle?.data?.data?.parts?.length > 0 ?
        singleVehicle.data.data.parts.map((part) => part.categoryId)
          .filter((id): id is string | null => id !== undefined)
        : []
    );

    // filter out categories that already exist on the vehicle
    // but keep manually added ones (they'll be merged later)
    return built.filter(cat => !existingCategoryIds.has(cat.category.id));
  }, [
    categorySummaries.data,
    singleVehicle.data,
    assemblies.data,
    lookup.data,
    marketData.data,
    allCategories.data
  ]);


  useEffect(() => {
    const isLoading = categorySummaries.isLoading || singleVehicle.isLoading || assemblies.isLoading;
    if (isLoading) return;

    setCategories(prev => {
      const existingManual = prev.filter(c => c.isManual);
      const existingIds = new Set(baseCategories?.map(c => c.category.id));
      const safeManual = existingManual.filter(m => !existingIds.has(m.category.id));

      const mergedCategories = [...baseCategories, ...safeManual].map(newCat => {
        const existingCat = prev.find(c => c.category.id === newCat.category.id);
        if (!existingCat) {
          return {
            ...newCat,
            category: getCategoryWithMasterNames(newCat.category),
          };
        }

        return {
          ...newCat,
          // Preserve user-set price
          ...(existingCat.price ? { price: existingCat.price } : {}),
          // Preserve RS enrichment (set directly via setCategories, not in baseCategories)
          category: getCategoryWithMasterNames({
            ...newCat.category,
            ...(newCat.category.lookupAveragePrice == null && existingCat.category.lookupAveragePrice != null ? {
              lookupAveragePrice: existingCat.category.lookupAveragePrice,
              lookupPartNumber: existingCat.category.lookupPartNumber,
            } : {}),
            ...(existingCat.category.rsPrice != null ? {
              rsPrice: existingCat.category.rsPrice,
              rsItem: existingCat.category.rsItem,
              rsData: existingCat.category.rsData,
            } : {}),
          }),
        };
      });

      return mergedCategories;
    });

    setIsLoading(false);
  }, [baseCategories, categorySummaries.isLoading, singleVehicle.isLoading, assemblies.isLoading]);

  const handlePartProLookup = () => {

    setPartProLoading(true);
    lookupMutation.mutate(hasLookup,
      {
        onSuccess: (data) => {
          addNotification({ primaryText: "PartPro Lookup Successful", variant: "success", });
          setCategories(prev => mergeLookup(prev, data.data));
          setPartProLoading(false);
        },
        onError: (error) => {
          console.error("PartPro lookup failed:", error);
          addNotification({ primaryText: "PartPro Lookup Failed", variant: "error", })
          setPartProLoading(false);
        }
      }
    );

    setPartProOpen(false);
  }

  // whenever we update the selected categories 
  // we want to move that new data back to categories too
  useEffect(() => {
    setCategories(prev => {
      // find the matching category in selectedCategories and update the category data with it, otherwise keep the same
      const newArray = prev.map(cat => {
        const selected = selectedCategories.find(c => c.category.id === cat.category.id);
        if (selected) {
          console.log("Updating category", cat.category.name, "with price", selected.price);
          return { ...cat, category: { ...cat.category, price: selected.price } };
        }
        return cat;
      });
      return newArray;
    });
  }, [selectedCategories, setSelectedCategories]);

  useEffect(() => {
    if (categories && categories.length > 0) {
      const sorted = [...categories].sort((a, b) => {
        // Use lastSoldPrice from summary if available, otherwise from category
        const aLastSold = a.lastSoldPrice ?? a.category.lastSoldPrice ?? 0;
        const aMax =
          Math.max(
            aLastSold,
            a.category.marketDataAveragePrice || 0,
            a.category.lookupAveragePrice || 0,
            a.category.rsPrice || 0
          );
        const bLastSold = b.lastSoldPrice ?? b.category.lastSoldPrice ?? 0;
        const bMax =
          Math.max(
            bLastSold,
            b.category.marketDataAveragePrice || 0,
            b.category.lookupAveragePrice || 0,
            b.category.rsPrice || 0
          );

        // Sort highest to lowest
        return bMax - aMax;
      });

      setSortedCategories(sorted);
    }
  }, [categories]);



  const blackListed = useMemo(() => {
    const make = vehicle?.combinedMake?.toLowerCase();

    return PartProBlackList.includes(make ?? "") ? true : false;
  }, [vehicle])

  if (isLoading) return <LoadingWheel />;

  return (
    <>
      <Modal open={addManualOpen} width={"max-w-6xl"} setOpen={setAddManualOpen}>
        <PartCategoryCombo handleAddCategory={handleAddCategory} shouldFocus />
      </Modal>

      <ConfirmModal
        open={partProOpen} setOpen={setPartProOpen}
        onConfirm={handlePartProLookup}
        isLoading={partProLoading}
        title={"Activate PartPro Lookup?"}
        message={`This will start a PartPro lookup on this vehicle, this may take some time.\n ${hasLookup ? "This lookup has already been completed for this vehicle, but you can run it again if you want to refresh the data." : ""}
              This will check against an exact part number match, display it for you in the table, show how many it matched on and give you an accurate price estimate for the parts.\n
              This costs 1 token per lookup, you have ${brand.data?.data.lookupTokens ?? 0} tokens remaining.`}
        confirmButtonText={null}
      />

      <ConfirmModal
        open={rsOpen} setOpen={setRsOpen}
        onConfirm={() => handleRsLookup(false)}
        isLoading={rsLoading}
        title={"RS Automotive Lookup"}
        message={`This will start a RS Automotive lookup on this vehicle, this may take some time.\n
              This does not cost any tokens but requires an RS Automotive API key to be set up by your administrator.`}
        confirmButtonText={null}
        Icon={<InformationCircleIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />}
        iconBg="bg-blue-100"
      />

      <PreviousPartListModal
        open={previousPartListOpen}
        setOpen={setPreviousPartListOpen}
        previousPartListData={previousPartListData}
        previousPartListsData={previousPartListsData}
        selectedPreviousJobId={selectedPreviousJobId}
        setSelectedPreviousJobId={setSelectedPreviousJobId}
        categories={categories}
        allCategories={allCategories.data?.data}
        onApply={applyPreviousPartListCategories}
      />

      <div className="flex flex-col gap-2">

        {!hidePartsInStock && (
          <div>
            <PartsInStockTable
              rows={categoryStockTable.data?.data ?? []}
              isLoading={categoryStockTable.isLoading || categoryStockTable.isFetching}
            />
          </div>
        )}

        <div className="flex items-center justify-between mt-4">

          <div className="flex gap-x-4">
            <ButtonInput
              label={"Add Manual Category"}
              isSubmit={false}
              onClick={() => setAddManualOpen(true)}
              classes={""}
            />

            <ButtonInput
              label={"Top 5"}
              isSubmit={false}
              onClick={() => setTopCategories(5)}
              classes={""}
            />

            <ButtonInput
              label={"Top 10"}
              isSubmit={false}
              onClick={() => setTopCategories(10)}
              classes={""}
            />

            {hasRsKey && (
              <ButtonInput
                label={`RS (£${rsTotal.toFixed(2)})`}
                isSubmit={false}
                onClick={() => setRsCategories()}
                classes={"!bg-[#69141E] !text-white hover:!bg-[#4a0f14]"}
              />
            )}
          </div>

          <div
            onMouseOver={() => setIsHovering(true)}
            onMouseOut={() => setIsHovering(false)}
            className="relative"
          >
            {isHovering && blackListed && (
              <div className="absolute bottom-10 w-[250px] bg-white border-2 border-gray-200 right-full">
                <p className="text-red-700 p-2">
                  PartPro lookups don't currently support this manufacturer
                </p>
              </div>
            )}
            <ButtonInput
              isSubmit={false}
              onClick={() => setPartProOpen(true)}
              classes={classNames(partProLoading || partProUnlocked || blackListed ? "!bg-gray-400 !text-gray-500 hover:!cursor-default" : "!bg-wilma-500 text-white hover:!bg-wilma-600 hover:!text-white")}
              disabled={partProLoading || blackListed}
            >
              <span className="flex items-center gap-x-2 p-2 px-4">
                PartPro Lookup
                <span className=""><KeyIcon height={15} width={15} /></span>
              </span>
            </ButtonInput>

            {hasRsKey && (
              <ButtonInput
                isSubmit={false}
                onClick={() => setRsOpen(true)}
                // classes={classNames("!bg-[#febe02] text-white hover:!bg-[#dfa600] hover:!text-white transition-colors duration-200", "ml-4")}
                classes={classNames("!bg-[#69141E] !text-white hover:!bg-[#4a0f14]", "ml-4")}
                disabled={false}
              >
                <span className="flex items-center gap-x-2 p-2 px-4">
                  RS Automotive Lookup
                  <span className=""><MagnifyingGlassIcon height={15} width={15} /></span>
                </span>
              </ButtonInput>
            )}
            <ButtonInput
              isSubmit={false}
              onClick={() => fetchPreviousPartListCategories()}
              classes={classNames("!bg-primary-600 !text-white hover:!bg-primary-700", hasRsKey ? "ml-2" : "ml-4")}
              disabled={previousPartListLoading || !vehicle?.id}
              isLoading={previousPartListLoading}
            >
              <span className="flex items-center gap-x-2 p-2 px-4">
                View Previous Part List
              </span>
            </ButtonInput>
          </div>

        </div>

        <SelectedCategories
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          setSelectedByTableId={setSelectedByTableId}
          lead={lead}
          vehicle={vehicle}
        />

        <div className="mt-1 flex items-center justify-between gap-3">
          <div>
            {hasRsKey && (
              <ButtonGroup
                options={options}
                selectedIndex={selectedIndex}
                onChange={(id) => setTab(id as "all" | "rs")}
              />
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <SearchByFiltersModal
              buttonLabel="Search By (Body)"
              options={bodySearchOptions}
              searchBy={bodySearchBy}
              setSearchBy={setBodySearchBy}
              isLoading={categorySummaries.isFetching}
              applyOnClose={true}
              disabled={categorySummaries.isLoading}
              modalWidth="sm:max-w-lg"
              onOpenChange={(open) => {
                if (open) {
                  setDraftBodyYearFrom(bodyYearFrom);
                  setDraftBodyYearTo(bodyYearTo);
                  return;
                }
                setBodyYearFrom(draftBodyYearFrom);
                setBodyYearTo(draftBodyYearTo);
              }}
              extraContent={
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900">Year range</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min={1900}
                      max={2100}
                      value={draftBodyYearFrom ?? ""}
                      onChange={(e) => setDraftBodyYearFrom(e.target.value === "" ? undefined : Number(e.target.value))}
                      disabled={categorySummaries.isLoading}
                      className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
                      placeholder="From"
                    />
                    <input
                      type="number"
                      min={1900}
                      max={2100}
                      value={draftBodyYearTo ?? ""}
                      onChange={(e) => setDraftBodyYearTo(e.target.value === "" ? undefined : Number(e.target.value))}
                      disabled={categorySummaries.isLoading}
                      className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
                      placeholder="To"
                    />
                  </div>
                </div>
              }
            />
            <SearchByFiltersModal
              buttonLabel="Search By (Mech)"
              options={mechSearchOptions}
              searchBy={mechSearchBy}
              setSearchBy={setMechSearchBy}
              isLoading={categorySummaries.isFetching}
              applyOnClose={true}
              disabled={categorySummaries.isLoading}
              modalWidth="sm:max-w-lg"
              onOpenChange={(open) => {
                if (open) {
                  setDraftMechYearFrom(mechYearFrom);
                  setDraftMechYearTo(mechYearTo);
                  return;
                }
                setMechYearFrom(draftMechYearFrom);
                setMechYearTo(draftMechYearTo);
              }}
              extraContent={
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900">Year range</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min={1900}
                      max={2100}
                      value={draftMechYearFrom ?? ""}
                      onChange={(e) => setDraftMechYearFrom(e.target.value === "" ? undefined : Number(e.target.value))}
                      disabled={categorySummaries.isLoading}
                      className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
                      placeholder="From"
                    />
                    <input
                      type="number"
                      min={1900}
                      max={2100}
                      value={draftMechYearTo ?? ""}
                      onChange={(e) => setDraftMechYearTo(e.target.value === "" ? undefined : Number(e.target.value))}
                      disabled={categorySummaries.isLoading}
                      className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
                      placeholder="To"
                    />
                  </div>
                </div>
              }
            />
            <SearchByFiltersModal
              buttonLabel="Part Status"
              options={categoryStatusSearchOptions}
              searchBy={categoryStatusSearchBy}
              setSearchBy={setCategoryStatusSearchBy}
              isLoading={categorySummaries.isFetching}
              applyOnClose={true}
              disabled={categorySummaries.isLoading}
              modalWidth="sm:max-w-lg"
            />
          </div>
        </div>

        <ManualLeadPartCategories
          selectedCategories={selectedCategories}
          handleTableSelectionChange={handleTableSelectionChange}
          partProUnlocked={partProUnlocked}
          partProLoading={partProLoading}
          vehicle={vehicle!}
          filter={filter}
          leadPartsTableFilters={leadPartsTableFilters}
          showBestPrice={hasTmeFeature}
        />

        <LeadPartsCategories
          categories={categories ?? []}
          selectedCategories={selectedCategories}
          handleTableSelectionChange={handleTableSelectionChange}
          partProUnlocked={partProUnlocked}
          partProLoading={partProLoading}
          vehicle={vehicle!}
          filter={filter}
          leadPartsTableFilters={leadPartsTableFilters}
          showBestPrice={hasTmeFeature}
        />
      </div>
    </>
  )
}
